// Shared presentation tokens for a hiring role, used by both places a role is
// rendered: the inline panel under the Job Board map
// (components/hiring/job-role-panel.tsx) and the standalone detail page
// (app/[locale]/hiring/[slug]/page.tsx).
//
// They lived inline in the panel first and the two surfaces promptly drifted —
// different chip sizes, two different Active badges, two section rhythms — for
// what is the same content. One module, so a change to the role's look lands
// on both.

// One chip geometry for every emblem on a role: position, compensation,
// experience, specialty, clientele. Tones share a single recipe — -50 fill,
// -200 hairline, -800 label — with dark-mode pairs, so no hue reads lighter
// or heavier than its neighbours.
export const CHIP =
  "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium leading-none"

export const CHIP_TONE = {
  amber: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-400/25",
  sky: "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-500/10 dark:text-sky-200 dark:border-sky-400/25",
  neutral: "bg-zinc-50 text-zinc-700 border-zinc-200 dark:bg-zinc-500/10 dark:text-zinc-200 dark:border-zinc-400/25",
  violet: "bg-violet-50 text-violet-800 border-violet-200 dark:bg-violet-500/10 dark:text-violet-200 dark:border-violet-400/25",
  green: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:border-emerald-400/25",
} as const

// Section label + body rhythm. Every labelled block (About this role,
// Specialties, Preferred clientele) uses the same eyebrow and the same gap, so
// a role reads as one stack rather than three differently-spaced ones. The
// heading sits quieter than the body it introduces — that is what makes the
// body read as primary.
export const SECTION = "mb-7"
export const EYEBROW =
  "text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground mb-2.5"

// "Active" label beside the PulseDot lozenge.
export const ACTIVE_LABEL =
  "text-[11px] font-semibold uppercase tracking-[0.08em] text-emerald-700 dark:text-emerald-400"

// Secondary action ("View <shop>") — darker than the muted-foreground/
// font-light treatment it replaced, which read as disabled next to the
// Inquire CTA.
export const SECONDARY_LINK =
  "inline-flex items-center gap-1.5 text-[13px] font-medium text-foreground/70 hover:text-foreground transition-colors"

// Owner's "Edit posting" shortcut — an outline button the same height as the
// filled Inquire pill it stands in for.
export const EDIT_BUTTON =
  "inline-flex items-center justify-center gap-1.5 h-9 px-5 rounded-full text-xs font-medium tracking-wide border border-foreground/15 text-foreground hover:bg-foreground/5 transition-colors"
